import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AIClipHistoryPage,
  AIClipTaskSummary,
  cancelAIClipTask,
  listAIClipTasks,
  triggerAIClip,
} from '../../../api/aiClip';
import { isApiError } from '../../../api/errors';
import Modal from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import AIClipHistoryList from './AIClipHistoryList';
import AIClipResultView from './AIClipResultView';
import AIClipTaskActionCard from './AIClipTaskActionCard';
import './AIClipWorkspace.css';

const PAGE_LIMIT = 20;
const POLL_INTERVAL_MS = 30_000;

export interface AIClipWorkspaceSummary {
  activeTask: AIClipTaskSummary | null;
  total: number;
  submissionPending?: boolean;
}

interface AIClipWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  gateReady: boolean;
  summary?: AIClipWorkspaceSummary | null;
  onSummaryChange: (summary: AIClipWorkspaceSummary) => void;
}

export function mergeTasks(current: AIClipTaskSummary[], incoming: AIClipTaskSummary[]) {
  const byId = new Map(current.map(task => [task.id, task]));
  incoming.forEach(task => byId.set(task.id, task));
  return Array.from(byId.values()).sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
}

const AIClipWorkspaceModal = ({
  open, onClose, episodeId, gateReady, summary, onSummaryChange,
}: AIClipWorkspaceModalProps) => {
  const { t } = useTranslation('episodeDetail');
  const toast = useToast();
  const [mode, setMode] = useState<'history' | 'result'>('history');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<AIClipTaskSummary[]>([]);
  const [activeTask, setActiveTask] = useState<AIClipTaskSummary | null>(summary?.activeTask ?? null);
  const [total, setTotal] = useState(summary?.total ?? 0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [historyPhase, setHistoryPhase] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [postTriggerRefreshPending, setPostTriggerRefreshPending] = useState(
    summary?.submissionPending ?? false,
  );
  const [pollNonce, setPollNonce] = useState(0);
  const [premiereDownloadUrl, setPremiereDownloadUrl] = useState<string | undefined>();
  const historyScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef(0);
  const toastErrorRef = useRef(toast.error);

  useEffect(() => { toastErrorRef.current = toast.error; }, [toast.error]);

  const publishSummary = useCallback((
    nextActive: AIClipTaskSummary | null,
    nextTotal: number,
    submissionPending = false,
  ) => {
    onSummaryChange({ activeTask: nextActive, total: nextTotal, submissionPending });
  }, [onSummaryChange]);

  const applyFirstPage = useCallback((
    page: AIClipHistoryPage,
    replace: boolean,
    updateTailCursor: boolean,
  ) => {
    setTasks(current => replace ? page.items : mergeTasks(current, page.items));
    setActiveTask(page.activeTask);
    if (updateTailCursor) setNextCursor(page.nextCursor);
    const nextTotal = page.total ?? page.items.length;
    setTotal(nextTotal);
    publishSummary(page.activeTask, nextTotal);
  }, [publishSummary]);

  const refreshFirstPage = useCallback(async (replace = false, updateTailCursor = false) => {
    const page = await listAIClipTasks(episodeId, { limit: PAGE_LIMIT });
    applyFirstPage(page, replace, updateTailCursor);
    return page;
  }, [applyFirstPage, episodeId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setMode('history');
    setSelectedTaskId(null);
    setPremiereDownloadUrl(undefined);
    setTasks([]);
    setActiveTask(null);
    setTotal(0);
    setNextCursor(null);
    setLoadMoreFailed(false);
    setHistoryPhase('loading');
    listAIClipTasks(episodeId, { limit: PAGE_LIMIT })
      .then(page => {
        if (!cancelled) {
          applyFirstPage(page, true, true);
          setHistoryPhase('loaded');
        }
      })
      .catch(err => {
        if (!cancelled) {
          setHistoryPhase('failed');
          toastErrorRef.current(err instanceof Error ? err.message : String(err));
        }
      });
    return () => { cancelled = true; };
  }, [applyFirstPage, episodeId, open]);

  useEffect(() => {
    if (!open) setPostTriggerRefreshPending(summary?.submissionPending ?? false);
  }, [open, summary?.submissionPending]);

  useEffect(() => {
    if (!open || (!activeTask && !postTriggerRefreshPending)) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const page = await listAIClipTasks(episodeId, { limit: PAGE_LIMIT });
        if (!cancelled) {
          applyFirstPage(page, false, false);
          setHistoryPhase('loaded');
          setPostTriggerRefreshPending(false);
        }
      } catch (err) {
        if (!cancelled) setPollNonce(value => value + 1);
      }
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeTask, applyFirstPage, episodeId, open, pollNonce, postTriggerRefreshPending]);

  useLayoutEffect(() => {
    if (mode === 'history' && historyScrollRef.current) {
      historyScrollRef.current.scrollTop = savedScrollTop.current;
    }
  }, [mode]);

  const handleLoadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const page = await listAIClipTasks(episodeId, { limit: PAGE_LIMIT, cursor: nextCursor });
      setTasks(current => mergeTasks(current, page.items));
      setNextCursor(page.nextCursor);
      if (page.activeTask) {
        setActiveTask(page.activeTask);
        publishSummary(page.activeTask, total);
      }
    } catch (err) {
      setLoadMoreFailed(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRetryHistory = async () => {
    setHistoryPhase('loading');
    try {
      await refreshFirstPage(true, true);
      setHistoryPhase('loaded');
      setPostTriggerRefreshPending(false);
    } catch (err) {
      setHistoryPhase('failed');
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleTrigger = async () => {
    setIsTriggering(true);
    try {
      await triggerAIClip(episodeId);
      setPostTriggerRefreshPending(true);
      publishSummary(null, total, true);
      setHistoryPhase('loading');
      try {
        await refreshFirstPage(false);
        setHistoryPhase('loaded');
        setPostTriggerRefreshPending(false);
      } catch (err) {
        setHistoryPhase('failed');
        toast.error(err instanceof Error ? err.message : String(err));
      }
    } catch (err) {
      if (isApiError(err) && err.status === 409 && err.code === 'AI_CLIP_ACTIVE' && err.body?.activeTask) {
        const raced = err.body.activeTask as AIClipTaskSummary;
        setPostTriggerRefreshPending(false);
        setActiveTask(raced);
        setTasks(current => mergeTasks(current, [raced]));
        const nextTotal = Math.max(total, 1);
        setTotal(nextTotal);
        publishSummary(raced, nextTotal);
      } else {
        toast.error(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setIsTriggering(false);
    }
  };

  const handleCancel = async (taskId: string) => {
    setIsCancelling(true);
    try {
      const next = await cancelAIClipTask(episodeId, taskId);
      setTasks(current => mergeTasks(current, [next]));
      const nextActive = next.status === 'cancelled' ? null : next;
      setActiveTask(nextActive);
      publishSummary(nextActive, total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setIsCancelling(false);
    }
  };

  const selectResult = (taskId: string) => {
    savedScrollTop.current = historyScrollRef.current?.scrollTop ?? 0;
    setPremiereDownloadUrl(undefined);
    setSelectedTaskId(taskId);
    setMode('result');
  };

  const backToHistory = () => {
    setPremiereDownloadUrl(undefined);
    setMode('history');
    setSelectedTaskId(null);
  };

  const isResultMode = mode === 'result' && selectedTaskId !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={(
        <span className="ai-clip-workspace-heading">
          {isResultMode && (
            <button type="button" className="btn btn-ghost ai-clip-title-back" onClick={backToHistory}>
              {t('aiClip.workspace.back')}
            </button>
          )}
          <span className="ai-clip-workspace-title">
            <span aria-hidden="true">✦</span>
            {t('aiClip.workspace.title')}
          </span>
        </span>
      )}
      titleExtra={isResultMode && premiereDownloadUrl ? (
        <a href="https://drama.moyuai.com.cn/download-episodes" className="btn btn-secondary ai-clip-title-download">
          {t('aiClip.workspace.downloadProject')}
        </a>
      ) : undefined}
      width="920px"
      contentStyle={{ padding: '1rem 1.1rem', borderRadius: '16px' }}
      bodyStyle={{ overflow: 'hidden' }}
    >
      <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {activeTask?.cancelRequestedAt
          ? t('aiClip.workspace.cancelling')
          : activeTask ? t(`aiClip.workspace.status.${activeTask.status}`) : t('aiClip.workspace.history')}
      </span>
      {mode === 'result' && selectedTaskId ? (
        <div
          data-testid="result-scroll"
          className="ai-clip-result-scroll"
          style={{ overflowY: 'auto' }}
        >
          <AIClipResultView
            episodeId={episodeId}
            taskId={selectedTaskId}
            onPremiereDownloadUrlChange={setPremiereDownloadUrl}
          />
        </div>
      ) : (
        <div ref={historyScrollRef} data-testid="history-scroll" className="ai-clip-workspace-scroll">
          {historyPhase === 'loading' && <p className="ai-clip-workspace-message">{t('aiClip.workspace.loading')}</p>}
          {historyPhase === 'failed' && (
            <div role="alert" style={{ padding: '1rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--danger, #d33)' }}>{t('aiClip.workspace.loadFailed')}</p>
              <button type="button" className="btn btn-secondary" onClick={() => { void handleRetryHistory(); }}>
                {t('aiClip.workspace.retry')}
              </button>
            </div>
          )}
          {historyPhase === 'loaded' && (
            <>
              <AIClipTaskActionCard
                activeTask={activeTask}
                gateReady={gateReady}
                isTriggering={isTriggering}
                isCancelling={isCancelling}
                onTrigger={handleTrigger}
                onCancel={handleCancel}
              />
              <AIClipHistoryList
                tasks={tasks}
                hasMore={nextCursor !== null}
                isLoadingMore={isLoadingMore}
                loadMoreFailed={loadMoreFailed}
                onLoadMore={handleLoadMore}
                onSelectResult={selectResult}
              />
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AIClipWorkspaceModal;
